/**
 * Invite Accept Page
 * 
 * Handles invite token validation and routes user to registration/login.
 * Flow:
 * 1. Extract token from URL query param
 * 2. Validate token with backend
 * 3. Create short-lived invite session
 * 4. Show auth options (Google Sign-In or Local Registration)
 * 5. After auth, user account is created with invite roles
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useGoogleOAuth } from "../hooks/useGoogleOAuth";
import { normalizeError } from "../lib/normalizeError";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Package, AlertTriangle, Loader2, Mail, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";  // Empty = same origin (relative URLs)

// Google icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function InviteAccept() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  
  const [status, setStatus] = useState("validating"); // validating, valid, invalid, registering
  const [inviteData, setInviteData] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    password: "",
    confirmPassword: ""
  });

  const token = searchParams.get("token");

  // Validate invite token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setStatus("invalid");
        setError("No invitation token provided.");
        return;
      }

      try {
        const response = await axios.post(`${API}/api/invite/validate`, { token });
        
        if (response.data.valid) {
          setInviteData({
            email: response.data.email,
            roles: response.data.roles
          });
          setSessionId(response.data.session_id);
          setStatus("valid");
        } else {
          setStatus("invalid");
          setError(response.data.error || "Invalid invitation.");
        }
      } catch (err) {
        setStatus("invalid");
        setError(err.response?.data?.detail || "Failed to validate invitation.");
      }
    };

    validateToken();
  }, [token]);

  // Google OAuth for invited user
  const handleGoogleSuccess = async (accessToken) => {
    await loginWithToken(accessToken);
    toast.success("Welcome! Your account has been created.");
    navigate("/items", { replace: true });
  };

  const handleGoogleError = (error) => {
    console.error("OAuth error:", error);
    toast.error(error || "Google sign-in failed");
  };

  const { startOAuth, loading: googleLoading, oauthConfigured } = useGoogleOAuth({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError
  });

  // Handle Google sign-in with invite session
  const handleGoogleSignIn = () => {
    // Store invite session in localStorage for after OAuth redirect
    if (sessionId) {
      localStorage.setItem("invite_session_id", sessionId);
    }
    startOAuth();
  };

  // Handle local registration
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    
    try {
      // Register with invite session
      const response = await axios.post(`${API}/api/auth/register`, {
        email: inviteData.email,
        password: formData.password,
        name: formData.name,
        invite_session_id: sessionId
      });

      // Now login to get token
      const loginResponse = await axios.post(`${API}/api/auth/login`, {
        email: inviteData.email,
        password: formData.password
      });

      await loginWithToken(loginResponse.data.token, loginResponse.data.user);
      toast.success("Welcome! Your account has been created.");
      navigate("/items", { replace: true });
    } catch (err) {
      toast.error(normalizeError(err.response?.data) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // Render validating state
  if (status === "validating") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Render invalid state
  if (status === "invalid") {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: `linear-gradient(180deg, rgba(9, 9, 11, 0.95) 0%, rgba(9, 9, 11, 0.98) 100%)`
        }}
      >
        <Card className="w-full max-w-md bg-card/95 backdrop-blur-xl border-border rounded-sm">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-destructive/10 border border-destructive/30 rounded-sm flex items-center justify-center">
                <XCircle className="w-8 h-8 text-destructive" />
              </div>
            </div>
            <div>
              <CardTitle className="text-2xl font-heading">Invalid Invitation</CardTitle>
              <CardDescription className="text-muted-foreground mt-2">
                {error}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate("/login")}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render valid invite - show registration options
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(180deg, rgba(9, 9, 11, 0.95) 0%, rgba(9, 9, 11, 0.98) 100%)`
      }}
    >
      <Card className="w-full max-w-md bg-card/95 backdrop-blur-xl border-border rounded-sm">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 rounded-sm flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl font-heading">You're Invited!</CardTitle>
            <CardDescription className="text-muted-foreground mt-2">
              Complete your account setup to access Kanban Inventory
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Invite details */}
          <div className="p-4 bg-muted/30 border border-border rounded-sm space-y-2">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">{inviteData?.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Roles:</span>
              {inviteData?.roles?.map((role) => (
                <Badge key={role} variant="secondary" className="text-xs capitalize">
                  {role}
                </Badge>
              ))}
            </div>
          </div>

          {/* Google Sign-In */}
          {oauthConfigured && (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 flex items-center justify-center gap-3 rounded-sm"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                data-testid="google-signup-btn"
              >
                {googleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <GoogleIcon />
                )}
                <span>{googleLoading ? "Signing up..." : "Continue with Google"}</span>
              </Button>
              
              <div className="relative">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground uppercase">
                  or create password
                </span>
              </div>
            </>
          )}

          {/* Local Registration Form */}
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium tracking-wide uppercase text-muted-foreground">
                Your Name
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="h-11"
                data-testid="invite-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium tracking-wide uppercase text-muted-foreground">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
                className="h-11"
                data-testid="invite-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium tracking-wide uppercase text-muted-foreground">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                minLength={8}
                className="h-11"
                data-testid="invite-confirm-password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-11 uppercase tracking-wider font-semibold rounded-sm"
              disabled={loading}
              data-testid="invite-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground">
            By creating an account, you agree to the terms of service.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
